// ĐỌC CHỮ TỪ TỆP TÀI LIỆU cho hồ sơ sản phẩm — khách dạy AI bằng nhiều kiểu tệp: .txt, .md, .docx, .doc (Word 97–2003).
// Chạy trọn trên máy (panel), KHÔNG gửi tệp đi đâu. Nhận dạng theo NỘI DUNG (chữ ký đầu tệp) trước, đuôi tên sau: .doc thật ra là
// .docx (hay ngược lại) vẫn đọc đúng; tệp đổi đuôi bừa / hỏng / có mật khẩu → báo rõ bằng tiếng Việt, không đoán, không trả chữ rác.
// Giới hạn đã biết: .doc có "theo dõi thay đổi" thì chữ ĐÃ XOÁ vẫn còn (Word cất chúng trong luồng chữ, đánh dấu ở thuộc tính ký tự —
// không đọc thuộc tính ký tự). .docx thì bỏ đúng chữ đã xoá (<w:delText>). Người dùng luôn thấy chữ trong ô tài liệu trước khi lưu.

import { chuTuPdf, laPdf } from "./tai-lieu-pdf";

export const DUOI_TAI_LIEU = [".txt", ".md", ".doc", ".docx"] as const;
// Thư viện sản phẩm Boss đọc thêm PDF có lớp chữ (src/shared/tai-lieu-pdf.ts). Plugin giữ nguyên DUOI_TAI_LIEU (không bật pdf).
export const DUOI_TAI_LIEU_BOSS = [...DUOI_TAI_LIEU, ".pdf"] as const;
export interface TuyChonDoc { pdf?: boolean }
export const TAI_LIEU_TOI_DA_BYTE = 15 * 1024 * 1024;
const GIAI_NEN_TOI_DA = 60 * 1024 * 1024; // chống "bom zip": document.xml giải nén không quá chừng này

export class LoiTaiLieu extends Error {
  constructor(msg: string) { super(msg); this.name = "LoiTaiLieu"; }
}

const duoiCua = (ten: string): string => (/\.[^.\\/]+$/.exec(ten)?.[0] ?? "").toLowerCase();

// Kiểm đuôi + kích thước TRƯỚC khi đọc cả tệp vào bộ nhớ (panel gọi với File.size).
export function kiemTepTruoc(ten: string, soByte: number, tc: TuyChonDoc = {}): void {
  const duoi: readonly string[] = tc.pdf ? DUOI_TAI_LIEU_BOSS : DUOI_TAI_LIEU;
  if (!duoi.includes(duoiCua(ten))) throw new LoiTaiLieu(`'${ten}': chỉ đọc được tệp ${duoi.join(", ")}`);
  if (soByte > TAI_LIEU_TOI_DA_BYTE) throw new LoiTaiLieu(`'${ten}' quá lớn (${(soByte / 1048576).toFixed(1)} MB) — tối đa ${TAI_LIEU_TOI_DA_BYTE / 1048576} MB`);
}

export async function docTaiLieu(ten: string, data: Uint8Array, tc: TuyChonDoc = {}): Promise<string> {
  kiemTepTruoc(ten, data.length, tc);
  const duoi = duoiCua(ten);
  if (!data.length) throw new LoiTaiLieu(`'${ten}' là tệp rỗng`);
  let chu: string;
  try {
    if (tc.pdf && laPdf(data, duoi === ".pdf")) chu = await chuTuPdf(ten, data);
    else if (laZip(data)) chu = await chuTuDocx(ten, data);
    else if (laCfb(data)) chu = chuTuDoc(ten, data);
    else if (duoi === ".doc" || duoi === ".docx") throw new LoiTaiLieu(loiKhongPhaiWord(ten, data));
    else if (duoi === ".pdf") throw new LoiTaiLieu(`'${ten}' không phải tệp PDF hợp lệ (tệp hỏng hoặc bị đổi đuôi)`);
    else chu = chuTuVanBan(ten, data);
  } catch (e) {
    if (e instanceof LoiTaiLieu) throw e;
    // Cấu trúc tệp sai (trỏ ra ngoài tệp, chuỗi sector vòng…) → một lỗi rõ cho người dùng, kèm lý do kỹ thuật để tra.
    throw new LoiTaiLieu(`'${ten}' bị hỏng hoặc không đúng định dạng (${e instanceof Error ? e.message : String(e)})`);
  }
  chu = donDep(chu);
  if (!chu) throw new LoiTaiLieu(`'${ten}' không có chữ nào đọc được`);
  return chu;
}

function donDep(s: string): string {
  return s.normalize("NFC")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/[ \t ]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ---------------- .txt / .md ----------------
function chuTuVanBan(ten: string, d: Uint8Array): string {
  if (d[0] === 0xef && d[1] === 0xbb && d[2] === 0xbf) return new TextDecoder("utf-8").decode(d.subarray(3));
  if (d[0] === 0xff && d[1] === 0xfe) return new TextDecoder("utf-16le").decode(d.subarray(2));
  if (d[0] === 0xfe && d[1] === 0xff) return new TextDecoder("utf-16be").decode(d.subarray(2));
  const dau = d.subarray(0, 4096);
  if (dau.filter((b) => b === 0).length > dau.length / 50) throw new LoiTaiLieu(`'${ten}' không phải tệp chữ (có vẻ là tệp nhị phân đổi đuôi)`);
  try { return new TextDecoder("utf-8", { fatal: true }).decode(d); }
  catch { return new TextDecoder("windows-1258").decode(d); } // tệp chữ tiếng Việt kiểu cũ (Windows-1258: chữ gốc + dấu thanh rời)
}

function loiKhongPhaiWord(ten: string, d: Uint8Array): string {
  const dau = new TextDecoder("latin1").decode(d.subarray(0, 512)).trimStart().toLowerCase();
  if (dau.startsWith("{\\rtf")) return `'${ten}' thực ra là tệp RTF — mở bằng Word rồi "Lưu thành" .docx, sau đó tải lại`;
  if (dau.startsWith("<") && /<html|<!doctype html|<\?xml/.test(dau)) return `'${ten}' thực ra là trang web/XML — mở bằng Word rồi "Lưu thành" .docx, sau đó tải lại`;
  return `'${ten}' không phải tệp Word hợp lệ (tệp hỏng hoặc bị đổi đuôi)`;
}

// ---------------- .docx (zip) ----------------
const u16 = (d: Uint8Array, o: number): number => { if (o + 2 > d.length) throw new Error(`đọc quá cuối tệp tại ${o}`); return d[o]! | (d[o + 1]! << 8); };
const u32 = (d: Uint8Array, o: number): number => { if (o + 4 > d.length) throw new Error(`đọc quá cuối tệp tại ${o}`); return (d[o]! | (d[o + 1]! << 8) | (d[o + 2]! << 16) | (d[o + 3]! << 24)) >>> 0; };

function laZip(d: Uint8Array): boolean { return d[0] === 0x50 && d[1] === 0x4b && (d[2] === 3 || d[2] === 5) && (d[3] === 4 || d[3] === 6); }

interface MucZip { ten: string; phuongPhap: number; co: number; nen: number; goc: number; viTri: number }
function mucLucZip(d: Uint8Array): MucZip[] {
  let eocd = -1;
  for (let i = d.length - 22; i >= Math.max(0, d.length - 22 - 65535); i--) if (u32(d, i) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("không thấy mục lục zip");
  const soMuc = u16(d, eocd + 10);
  let p = u32(d, eocd + 16);
  if (p === 0xffffffff) throw new Error("zip64 chưa hỗ trợ");
  const out: MucZip[] = [];
  for (let i = 0; i < soMuc; i++) {
    if (u32(d, p) !== 0x02014b50) throw new Error("mục lục zip sai");
    const nl = u16(d, p + 28), el = u16(d, p + 30), cl = u16(d, p + 32);
    out.push({ ten: new TextDecoder().decode(d.subarray(p + 46, p + 46 + nl)), co: u16(d, p + 8), phuongPhap: u16(d, p + 10), nen: u32(d, p + 20), goc: u32(d, p + 24), viTri: u32(d, p + 42) });
    p += 46 + nl + el + cl;
  }
  return out;
}

async function layTepZip(tenTep: string, d: Uint8Array, m: MucZip): Promise<Uint8Array> {
  if (m.co & 1) throw new LoiTaiLieu(`'${tenTep}' có mật khẩu — mở bằng Word, bỏ mật khẩu rồi tải lại`);
  if (m.goc > GIAI_NEN_TOI_DA) throw new LoiTaiLieu(`'${tenTep}' có phần nội dung quá lớn (${Math.round(m.goc / 1048576)} MB)`);
  if (u32(d, m.viTri) !== 0x04034b50) throw new Error("đầu mục zip sai");
  const bd = m.viTri + 30 + u16(d, m.viTri + 26) + u16(d, m.viTri + 28);
  if (bd + m.nen > d.length) throw new Error("mục zip bị cắt cụt");
  const nen = d.subarray(bd, bd + m.nen);
  if (m.phuongPhap === 0) return nen;
  if (m.phuongPhap !== 8) throw new Error(`kiểu nén zip ${m.phuongPhap} chưa hỗ trợ`);
  const s = new Blob([new Uint8Array(nen)]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(s).arrayBuffer());
}

async function chuTuDocx(ten: string, d: Uint8Array): Promise<string> {
  const muc = mucLucZip(d);
  const tim = (t: string) => muc.find((m) => m.ten === t);
  // Phần chính của tài liệu: theo _rels/.rels (chuẩn), mặc định word/document.xml.
  let duong = "word/document.xml";
  const rels = tim("_rels/.rels");
  if (rels) {
    const x = new TextDecoder().decode(await layTepZip(ten, d, rels));
    const r = /<Relationship\b[^>]*Type="[^"]*\/officeDocument"[^>]*>/.exec(x)?.[0];
    const t = r && /Target="\/?([^"]+)"/.exec(r)?.[1];
    if (t) duong = t;
  }
  const chinh = tim(duong);
  if (!chinh) {
    if (tim("xl/workbook.xml")) throw new LoiTaiLieu(`'${ten}' là tệp Excel, không phải Word — lưu phần chữ ra .docx hoặc .txt rồi tải lại`);
    if (tim("ppt/presentation.xml")) throw new LoiTaiLieu(`'${ten}' là tệp PowerPoint, không phải Word — lưu phần chữ ra .docx hoặc .txt rồi tải lại`);
    throw new LoiTaiLieu(`'${ten}' không phải tệp Word .docx (không có phần nội dung tài liệu)`);
  }
  return chuTuDocumentXml(new TextDecoder().decode(await layTepZip(ten, d, chinh)));
}

const THUC_THE: Record<string, string> = { lt: "<", gt: ">", amp: "&", quot: '"', apos: "'" };
function giaiThucThe(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|lt|gt|amp|quot|apos);/gi, (_, e: string) =>
    e[0] === "#" ? String.fromCodePoint(e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)) : THUC_THE[e.toLowerCase()]!);
}

// Chữ hiển thị của document.xml: <w:t> theo thứ tự; hết đoạn → xuống dòng; ô bảng → tab, hết hàng → xuống dòng.
// Bỏ: <w:delText> (chữ đã xoá khi theo dõi thay đổi), <w:instrText> (mã trường), <w:tabs><w:tab …/> (điểm dừng tab — không phải ký tự),
// và <mc:Fallback> (Word ghi hộp văn bản HAI lần: bản mới + bản dự phòng → đọc cả hai là chữ bị lặp).
export function chuTuDocumentXml(xml: string): string {
  const x = xml.replace(/<mc:Fallback\b[\s\S]*?<\/mc:Fallback>/g, "");
  let out = "";
  const re = /<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>|<w:tab\/>|<w:(?:br|cr)\b[^>]*\/>|<w:noBreakHyphen\/>|<\/w:p>|<w:p\b[^>]*\/>|<\/w:tc>|<\/w:tr>/g;
  for (let m = re.exec(x); m; m = re.exec(x)) {
    const tag = m[0];
    if (m[1] !== undefined) out += giaiThucThe(m[1]);
    else if (tag === "<w:tab/>") out += "\t";
    else if (tag.startsWith("<w:br") || tag.startsWith("<w:cr")) out += "\n";
    else if (tag === "<w:noBreakHyphen/>") out += "-";
    else if (tag === "</w:tc>") out = out.replace(/\n$/, "") + "\t";
    else if (tag === "</w:tr>") out = out.replace(/\t$/, "") + "\n";
    else out += "\n";
  }
  return out;
}

// ---------------- .doc (Word 97–2003: tệp ghép CFB) ----------------
function laCfb(d: Uint8Array): boolean {
  return d.length >= 512 && [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1].every((b, i) => d[i] === b);
}

const HET_CHUOI = 0xfffffffe, TRONG = 0xffffffff;
export function luongCfb(d: Uint8Array): Map<string, Uint8Array> {
  const coSector = 1 << u16(d, 0x1e), coMini = 1 << u16(d, 0x20);
  if (coSector !== 512 && coSector !== 4096) throw new Error(`cỡ sector ${coSector} lạ`);
  const sector = (n: number): Uint8Array => {
    const o = (n + 1) * coSector;
    if (o + coSector > d.length) throw new Error(`sector ${n} nằm ngoài tệp`);
    return d.subarray(o, o + coSector);
  };
  // Bảng FAT: 109 vị trí đầu ở header, phần còn lại theo chuỗi DIFAT.
  const soFat = u32(d, 0x2c);
  const viTriFat: number[] = [];
  for (let i = 0; i < 109 && viTriFat.length < soFat; i++) viTriFat.push(u32(d, 0x4c + i * 4));
  for (let s = u32(d, 0x44), vong = 0; s !== HET_CHUOI && s !== TRONG && viTriFat.length < soFat; vong++) {
    if (vong > 100_000) throw new Error("chuỗi DIFAT vòng");
    const sec = sector(s);
    for (let i = 0; i < coSector / 4 - 1 && viTriFat.length < soFat; i++) viTriFat.push(u32(sec, i * 4));
    s = u32(sec, coSector - 4);
  }
  const fat: number[] = [];
  for (const s of viTriFat) { const sec = sector(s); for (let i = 0; i < coSector; i += 4) fat.push(u32(sec, i)); }
  const docChuoi = (dau: number, bang: number[], lay: (n: number) => Uint8Array, co: number): Uint8Array => {
    const phan: Uint8Array[] = [];
    for (let s = dau; s !== HET_CHUOI && s !== TRONG; s = bang[s] ?? HET_CHUOI) {
      if (phan.length > bang.length) throw new Error("chuỗi sector vòng");
      phan.push(lay(s));
    }
    const out = new Uint8Array(phan.length * co);
    phan.forEach((p, i) => out.set(p, i * co));
    return out;
  };
  const thuMuc = docChuoi(u32(d, 0x30), fat, sector, coSector);
  const catNguong = u32(d, 0x38);
  const miniFatBytes = u32(d, 0x3c) === HET_CHUOI ? new Uint8Array(0) : docChuoi(u32(d, 0x3c), fat, sector, coSector);
  const miniFat: number[] = [];
  for (let i = 0; i + 4 <= miniFatBytes.length; i += 4) miniFat.push(u32(miniFatBytes, i));
  let miniStream: Uint8Array = new Uint8Array(0);
  const out = new Map<string, Uint8Array>();
  for (let o = 0; o + 128 <= thuMuc.length; o += 128) {
    const loai = thuMuc[o + 0x42];
    if (loai !== 2 && loai !== 5) continue;
    const dai = u16(thuMuc, o + 0x40);
    const ten = new TextDecoder("utf-16le").decode(thuMuc.subarray(o, o + Math.max(0, dai - 2)));
    const dau = u32(thuMuc, o + 0x74), co = u32(thuMuc, o + 0x78);
    if (loai === 5) { miniStream = docChuoi(dau, fat, sector, coSector).subarray(0, co); continue; }
    const byte = co < catNguong
      ? docChuoi(dau, miniFat, (n) => { const a = n * coMini; if (a + coMini > miniStream.length) throw new Error(`mini sector ${n} ngoài luồng`); return miniStream.subarray(a, a + coMini); }, coMini)
      : docChuoi(dau, fat, sector, coSector);
    if (byte.length < co) throw new Error(`luồng '${ten}' bị cắt cụt`);
    out.set(ten, byte.subarray(0, co));
  }
  return out;
}

function chuTuDoc(ten: string, d: Uint8Array): string {
  const luong = luongCfb(d);
  const wd = luong.get("WordDocument");
  if (!wd) {
    if (luong.has("EncryptedPackage")) throw new LoiTaiLieu(`'${ten}' có mật khẩu — mở bằng Word, bỏ mật khẩu rồi tải lại`);
    if (luong.has("Workbook") || luong.has("Book")) throw new LoiTaiLieu(`'${ten}' là tệp Excel, không phải Word — lưu phần chữ ra .docx hoặc .txt rồi tải lại`);
    if (luong.has("PowerPoint Document")) throw new LoiTaiLieu(`'${ten}' là tệp PowerPoint, không phải Word — lưu phần chữ ra .docx hoặc .txt rồi tải lại`);
    throw new LoiTaiLieu(`'${ten}' không phải tệp Word (không có phần nội dung tài liệu)`);
  }
  if (u16(wd, 0) !== 0xa5ec) throw new LoiTaiLieu(`'${ten}' không phải tệp Word hợp lệ`);
  if (u16(wd, 2) < 0xc1) throw new LoiTaiLieu(`'${ten}' là Word 6/95 quá cũ — mở bằng Word rồi "Lưu thành" .docx, sau đó tải lại`);
  const co = u16(wd, 0x0a);
  if (co & 0x0100) throw new LoiTaiLieu(`'${ten}' có mật khẩu — mở bằng Word, bỏ mật khẩu rồi tải lại`);
  const bang = luong.get(co & 0x0200 ? "1Table" : "0Table");
  if (!bang) throw new Error("thiếu luồng bảng (0Table/1Table)");
  // FIB: 32 byte cố định → csw + fibRgW → cslw + fibRgLw (ccpText ở ô 3) → cbRgFcLcb + các cặp fc/lcb (fcClx ở cặp 33).
  let o = 32;
  o += 2 + u16(wd, o) * 2;
  const soLw = u16(wd, o), dauLw = o + 2;
  o += 2 + soLw * 4;
  const ccpText = u32(wd, dauLw + 3 * 4);
  const soCap = u16(wd, o), dauCap = o + 2;
  if (soCap <= 33) throw new Error("FIB thiếu vị trí bảng mảnh");
  const fcClx = u32(wd, dauCap + 33 * 8), lcbClx = u32(wd, dauCap + 33 * 8 + 4);
  if (fcClx + lcbClx > bang.length) throw new Error("bảng mảnh nằm ngoài luồng bảng");
  let p = fcClx;
  while (p < fcClx + lcbClx && bang[p] === 0x01) p += 3 + (u16(bang, p + 1) << 16 >> 16); // bỏ các Prc (định dạng) đứng trước
  if (bang[p] !== 0x02) throw new Error("không thấy bảng mảnh (Pcdt)");
  const lcb = u32(bang, p + 1), plc = p + 5, n = (lcb - 4) / 12;
  if (!Number.isInteger(n) || n < 1) throw new Error("bảng mảnh sai kích thước");
  const dauPcd = plc + (n + 1) * 4;
  const cp1252 = new TextDecoder("windows-1252"), utf16 = new TextDecoder("utf-16le");
  let chu = "";
  for (let i = 0; i < n; i++) {
    const cp0 = u32(bang, plc + i * 4);
    if (cp0 >= ccpText) break; // chỉ phần thân chính (bỏ chú thích, đầu/chân trang…)
    const cch = Math.min(u32(bang, plc + (i + 1) * 4), ccpText) - cp0;
    const fcTho = u32(bang, dauPcd + i * 8 + 2);
    const nen = (fcTho & 0x40000000) !== 0, fc = fcTho & 0x3fffffff;
    const a = nen ? fc / 2 : fc, b = a + (nen ? cch : cch * 2);
    if (b > wd.length) throw new Error("mảnh chữ nằm ngoài luồng tài liệu");
    chu += nen ? cp1252.decode(wd.subarray(a, b)) : utf16.decode(wd.subarray(a, b));
  }
  return giaiMaKyTuWord(chu);
}

// Ký tự điều khiển trong luồng chữ Word: trường (0x13 mã 0x14 kết quả 0x15) → chỉ giữ KẾT QUẢ (chữ hiển thị của liên kết, số trang…);
// 0x0D hết đoạn, 0x0B xuống dòng mềm, 0x0C ngắt trang → xuống dòng; 0x07 hết ô → tab (hai 0x07 liền = hết hàng → xuống dòng).
function giaiMaKyTuWord(s: string): string {
  let out = "";
  const truong: boolean[] = []; // mỗi tầng: đã qua dấu tách (đang ở phần kết quả)?
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (c === 0x13) { truong.push(false); continue; }
    if (c === 0x14) { if (truong.length) truong[truong.length - 1] = true; continue; }
    if (c === 0x15) { truong.pop(); continue; }
    if (truong.some((k) => !k)) continue;
    if (c === 0x0d || c === 0x0b || c === 0x0c) out += "\n";
    else if (c === 0x07) out = out.endsWith("\t") ? out.slice(0, -1) + "\n" : out + "\t";
    else if (c === 0x1e) out += "-";
    else if (c === 0x09) out += "\t";
    else if (c >= 0x20) out += ch;
  }
  return out;
}
